import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { EscrowPartyTypeEnum, type IEscrowInvitation } from 'src/graphql/schema';
import { EscrowInvitationCreateDocument } from 'src/graphql/actions/escrow/escrowInvitationCreate.mutation';
import { EscrowInvitationUpdateDocument } from 'src/graphql/actions/escrow/escrowInvitationUpdate.mutation';
import { addEscrowInvitationCache } from 'src/graphql/client/cache';
import { type IEscrowInvitationForm } from '@/components/Escrow/Invitation';
import { ClientError, getApiError } from 'src/utils/errors';

import { useBeneficiaryInvitationContext } from './BeneficiaryInvitationContext';

// The first save creates the invitation, later saves (edits) update it.
// Resolves to whether it was saved; on success the card shows its details, on failure an error toast
export const useSaveInvitation = () => {
  const { escrowId, escrowInvitation, setMode } = useBeneficiaryInvitationContext();
  const [createInvitation] = useMutation(EscrowInvitationCreateDocument, {
    update: (_cache, { data }) => {
      const createdInvitation = data?.escrowInvitationCreate?.escrowInvitation;

      if (createdInvitation) addEscrowInvitationCache(createdInvitation as IEscrowInvitation);
    },
  });
  const [updateInvitation] = useMutation(EscrowInvitationUpdateDocument);

  const saveChanges = async (escrowInvitationId: string, values: IEscrowInvitationForm) => {
    const { data } = await updateInvitation({ variables: { escrowInvitationId, escrowInvitationParams: values } });

    if (!data?.escrowInvitationUpdate?.success) throw new ClientError(data?.escrowInvitationUpdate?.errors?.[0]);
  };

  const create = async (values: IEscrowInvitationForm) => {
    const { data } = await createInvitation({
      variables: { escrowId, escrowInvitationParams: { partyType: EscrowPartyTypeEnum.Beneficiary, ...values } },
    });

    if (!data?.escrowInvitationCreate?.success) throw new ClientError(data?.escrowInvitationCreate?.errors?.[0]);
  };

  return async (values: IEscrowInvitationForm) => {
    try {
      if (escrowInvitation) await saveChanges(escrowInvitation.id, values);
      else await create(values);

      toast.success('Invitation saved!');
      setMode('view');

      return true;
    } catch (e: unknown) {
      getApiError(e, toast.error);

      return false;
    }
  };
};
